import {useHistory} from '@aboutyou/router';
import React, {startTransition, useCallback, useEffect} from 'react';
import {useGetCoupons} from '../../features/Coupons/CouponWallet/hooks/useGetCoupons';
import {useActivatedCouponState} from '../../features/Coupons/useActivatedCouponState';
import {COUPON_PROMOTION_ID_QUERY_KEY} from '../../features/Coupons/useCouponPromotionIdFromQueryParams';
import {useProductIdFromSlug} from '../../features/ProductUrl';
import {MAX_TIMEOUT_WAIT_IN_MS} from '../../features/constants/constants';
import {useInvalidateRequests} from './useShowCouponsExpiredToast';

export const CouponsRefresh: React.FC = () => {
  const {data: couponsData, refetch: refetchCoupons} = useGetCoupons();
  const productId = useProductIdFromSlug();
  const {couponPromotionId: activeCouponPromotionId, setActivatedCoupon} =
    useActivatedCouponState(productId);
  const history = useHistory();
  const invalidateRequests = useInvalidateRequests();
  const nextUpdate = couponsData?.nextUpdateDate
    ? new Date(couponsData.nextUpdateDate).getTime()
    : undefined;

  const handleRefresh = useCallback(async () => {
    const currentCouponsSnapshot = JSON.stringify(couponsData?.coupon);
    const newCoupons = (await refetchCoupons()).data?.coupon;
    const newCouponsSnapshot = JSON.stringify(newCoupons);

    // if coupons are the same, we don't need to do anything
    if (currentCouponsSnapshot === newCouponsSnapshot) {
      return;
    }

    // check whether the selected coupon has expired
    // if it has, unselect it and remove if from query params
    if (activeCouponPromotionId) {
      const isActiveCouponStillAvailable = (newCoupons || []).some(
        coupon =>
          coupon.type?.active.couponPromotionId === activeCouponPromotionId,
      );

      if (!isActiveCouponStillAvailable) {
        setActivatedCoupon(undefined);
      }

      const queryParams = new URLSearchParams(history.location.search);

      if (queryParams.has(COUPON_PROMOTION_ID_QUERY_KEY)) {
        queryParams.delete(COUPON_PROMOTION_ID_QUERY_KEY);

        history.replace({
          search: queryParams.toString(),
        });
      }
    }

    invalidateRequests();
  }, [
    couponsData?.coupon,
    refetchCoupons,
    invalidateRequests,
    history,
    setActivatedCoupon,
    activeCouponPromotionId,
  ]);

  useEffect(() => {
    if (!nextUpdate) {
      return;
    }

    const timeNow = new Date().getTime();
    const timeDiff = nextUpdate - timeNow;

    const timeout = setTimeout(() => {
      // requestAnimationFrame is used to ensure that the refresh doesn't happen in background tabs.
      // The refresh will happen when the tab is active again.
      requestAnimationFrame(() => {
        startTransition(() => {
          handleRefresh();
        });
      });
    }, Math.min(timeDiff, MAX_TIMEOUT_WAIT_IN_MS));

    return () => clearTimeout(timeout);
  }, [nextUpdate, handleRefresh]);

  return null;
};
